import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import { FaArrowLeft, FaEdit, FaPrint } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const VoucherView = () => {
  const [receipts, setReceipts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [freesearch, setFreesearch] = useState("-");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const receiptsPerPage = 50;
  const [selectedRV, setSelectedRV] = useState(null);
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";

  useEffect(() => {
    fetchReceipts(currentPage, freesearch);
  }, [currentPage, freesearch]);

  const fetchReceipts = async (page = 1, search = "-") => {
    setIsLoading(true);
    setError(null);
    try {
      const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
      const currentDepartment =
        localStorage.getItem("currentSelectedDepartment") || "MAIN";

      // CHANGED: If search is standard "-" or empty, limit to 100. If searching, pass 1000.
      const isSearching = search !== "-" && search.trim() !== "";
      const limit = isSearching ? 1000 : 100;

      const apiUrl = `${apiPaths[0]?.API_CONFIG}/api/CRMDocListView/${COMPANY_CODE}/JOURNAL/-/-/-/${encodeURIComponent(search)}/-/${currentDepartment}/${page}/${limit}`;

      const response = await axios.get(apiUrl);
      setReceipts(response.data || []);
      console.log(`Fetched voucher list for page ${page} with limit ${limit}:`, response.data);
    } catch (err) {
      console.error("Error while fetching voucher List:", err);
      setError("Failed to fetch receipts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    // CHANGED: If search input is completely empty, default back to "-"
    if (term.trim() === "") {
      setFreesearch("-");
    } else {
      setFreesearch(term);
    }
    setCurrentPage(1);
  };

  // CHANGED: Dynamic total page determination matching modern slice limits
  const totalPages = Math.max(1, Math.ceil(receipts.length / receiptsPerPage));

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const navigate = useNavigate();
  const onBack = () => {
    navigate("/voucher");
  };

  const onEdit = (rec) => {
    console.log(" ~ onEdit ~ rec:", rec);

    const modifyVoucherData = {
      modifyVNO: rec.V_NO?.trim(),
      modifyVType: rec.JVTYPE,
      modifyVDate: dayjs(rec.V_DATE).format("YYYY-MM-DD"),
      amount: rec.AMOUNT,
      description: rec.DESRIPTION,
    };

    navigate("/voucher", { state: { modifyVoucherData } });
  };

  const onPrint = (data) => {
    console.log("Print JV :", data);
    setSelectedRV(data);
    const jvNo = data.V_NO.trim();
    console.log("jvNo :", jvNo);
    const jvType = data.JVTYPE;
    const jvDate = data.V_DATE;
    const formattedJvDate = new Date(jvDate).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    fetchJVPrintData(jvNo, jvType, formattedJvDate);
  };

  const fetchJVPrintData = async (jvNo, jvType, jvDate) => {
    const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    const currentDepartment =
      localStorage.getItem("currentSelectedDepartment") || "MAIN";

    try {
      const apiURL = `${apiPaths[0]?.API_CONFIG}/api/CRMReportGenerator/${COMPANY_CODE}/VOUCHER/${jvNo}/${currentDepartment}/-/${jvType}/-/-/-`;

      const response = await axios.get(apiURL);
      const data = response.data;
      console.log(`Fetched voucher print data:`, data);
      navigate(`/voucherPrint`, {
        state: {
          jvPrintData: data,
          jvNo: jvNo,
          jvType: jvType,
          jvDate: jvDate,
        },
      });
    } catch (err) {
      console.error("Error while fetching voucher print :", err);
      setError("Failed to fetch voucher print . Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center gap-6">
        <h2 className="text-xl text-gray-800 mb-4">Voucher View</h2>

        <div className="relative mb-4 w-[40%]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by any field..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            aria-label="Search receipts"
          />
        </div>
        <button
          onClick={onBack}
          className="mb-3 relative bg-gray-600 text-white font-medium text-[17px] px-2 py-[0.25em] pl-3 h-[1.8em] rounded-[0.9em] flex items-center overflow-hidden cursor-pointer group"
        >
          <span className="mr-10">Back</span>
          <div className="absolute right-[0.3em] bg-white h-[1.4em] w-[1.4em] rounded-[0.7em] flex items-center justify-center transition-all duration-300 group-hover:w-[calc(100%-0.6em)] active:scale-95">
            <FaArrowLeft className="text-gray-600" />
          </div>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8 my-8 inset-0">
          <div className="w-10 h-10 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto max-h-[70vh]">
            <table className="min-w-full table-auto border-collapse ">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-sm text-left font-medium text-gray-700">JV NO</th>
                  <th className="px-2 py-1.5 text-sm text-left font-medium text-gray-700">JV TYPE</th>
                  <th className="px-2 py-1.5 text-sm text-left font-medium text-gray-700">JV DATE</th>
                  <th className="px-2 py-1.5 text-sm text-left font-medium text-gray-700">JV MODE</th>
                  <th className="px-2 py-1.5 text-sm text-left font-medium text-gray-700">AMOUNT</th>
                  <th className="px-2 py-1.5 text-sm text-left font-medium text-gray-700">DESCRIPTION</th>
                  <th className="px-2 py-1.5 text-sm text-left font-medium text-gray-700">USER</th>
                  <th className="pl-2 pr-12 py-1.5 text-sm font-medium text-gray-700 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {receipts.length > 0 ? (
                  receipts.map((rec, index) => (
                    <tr key={index} className="hover:bg-blue-50 transition-colors">
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.V_NO}>{rec.V_NO?.trim()}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.JVTYPE}>{rec.JVTYPE}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.V_DATE}>{dayjs(rec.V_DATE).format("DD/MM/YYYY")}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.MODE}>{rec.MODE === "R" ? "Recurring" : "Non recurring"}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.AMOUNT}>{Number(rec.AMOUNT || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.DESRIPTION}>{rec.DESRIPTION}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.USER}>{rec.USER}</td>
                      <td className="px-2 py-1 border-b text-sm text-gray-600">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => onEdit(rec)}
                            className="px-2 py-1 rounded text-white bg-blue-600 flex items-center gap-1 text-sm"
                          >
                            <FaEdit className="text-white" size={12} /> Edit
                          </button>
                          <button
                            onClick={() => onPrint(rec)}
                            className="px-2 py-1 rounded text-white bg-yellow-600 flex items-center gap-1 text-sm"
                          >
                            <FaPrint className="text-white" size={12} /> Print
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-gray-500">No Receipts found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-600">
              Showing page {currentPage} ({receipts.length} receipts loaded)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-lg ${currentPage === 1 ? "bg-gray-200 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-lg ${currentPage === totalPages ? "bg-gray-200 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VoucherView;